import { produce } from 'immer';
import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import newslatterBannerImg from '../../../../../../common/assets/images/marketing/marketing-banner.png';
import Button from '../../../../../../common/widgets/Button';
import Dialog from '../../../../../../common/widgets/Dialog';
import Icons from '../../../../../../common/widgets/Icons';
import { CustomerInfoContext } from '../../../../contexts/CustomerInfoContext';
import {
  OptInChannelTypes,
  OptInSourceTypes,
  OptInTypes,
} from '../../../../enums';
import { resources } from '../../../../resources/resources';
import { optInService } from '../../../../services/opt-in';
import { showNewsletterErrorToast } from '../../../Common/Marketing/NewsletterBanner';

type Props = {
  onClose: () => void;
};

export default function NewsletterDialog({ onClose }: Readonly<Props>) {
  const { customerInfo, setCustomerInfo } = useContext(CustomerInfoContext);
  const { t: translate } = useTranslation();

  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const consentInProgressRef = useRef(false);

  const handleSubscribe = async () => {
    if (consentInProgressRef.current) return;

    consentInProgressRef.current = true;
    setIsSubscribing(true);

    try {
      const response = await optInService.consent(
        OptInTypes.NewsletterMarketing,
        true,
        OptInSourceTypes.PopupMyAccount,
      );

      if (response.hasErrors) {
        onClose();
        showNewsletterErrorToast(
          translate(resources.newsletter.banners.error.title),
          translate(resources.newsletter.banners.error.subtitle),
        );
        return;
      }

      setIsSuccess(true);

      setCustomerInfo(
        produce(customerInfo, draft => {
          draft.showNewsletterBanners = false;
          draft.showNewsletterPopup = false;
        }),
      );
    } finally {
      setIsSubscribing(false);
      consentInProgressRef.current = false;
    }
  };

  const handleCloseIconClick = async () => {
    if (consentInProgressRef.current) return;

    consentInProgressRef.current = true;

    try {
      const response = await optInService.consent(
        OptInTypes.NewsletterMarketing,
        false,
        OptInSourceTypes.PopupMyAccount,
        OptInChannelTypes.None,
        false,
      );

      if (response.hasErrors) {
        showNewsletterErrorToast(
          translate(resources.newsletter.banners.error.title),
          translate(resources.newsletter.banners.error.subtitle),
        );
        return;
      }

      setCustomerInfo(
        produce(customerInfo, draft => {
          draft.showNewsletterPopup = false;
        }),
      );

      onClose();
    } finally {
      consentInProgressRef.current = false;
    }
  };

  const handleDecline = async () => {
    if (consentInProgressRef.current) return;

    consentInProgressRef.current = true;
    setIsDeclining(true);

    try {
      const response = await optInService.consent(
        OptInTypes.NewsletterMarketing,
        false,
        OptInSourceTypes.PopupMyAccount,
        OptInChannelTypes.None,
        false,
      );

      if (response.hasErrors) {
        showNewsletterErrorToast(
          translate(resources.newsletter.banners.error.title),
          translate(resources.newsletter.banners.error.subtitle),
        );
        return;
      }

      setCustomerInfo(
        produce(customerInfo, draft => {
          draft.showNewsletterPopup = false;
        }),
      );

      onClose();
    } finally {
      setIsDeclining(false);
      consentInProgressRef.current = false;
    }
  };

  return (
    <Dialog
      closeDialog={handleCloseIconClick}
      showCloseIcon
      className="newsletter-dialog"
    >
      {isSuccess ? (
        <div className="newsletter-dialog-center">
          <div className="dialog-content">
            <div className="dialog-image-banner">
              <img src={newslatterBannerImg} alt="" />
            </div>

            <div className="dialog-title centered">
              {translate(resources.newsletter.banners.success.title)}{' '}
              <Icons.PartyEmoji />
            </div>

            <div className="dialog-text centered">
              {translate(resources.newsletter.banners.success.subtitle)}
            </div>
          </div>

          <div className="dialog-actions vertical full">
            <Button className="btn btn-solid" onClick={onClose}>
              Inchide
            </Button>
          </div>
        </div>
      ) : (
        <div className="newsletter-dialog-center">
          <div className="dialog-content">
            <div className="dialog-image-banner">
              <img src={newslatterBannerImg} alt="" />
            </div>

            <div className="dialog-title centered">
              {translate(resources.newsletter.dialog.title, {
                0: customerInfo.firstName,
              })}
            </div>

            <div className="dialog-text centered">
              {translate(resources.newsletter.dialog.description)}
            </div>

            <div className="dialog-disclaimer centered">
              {'*'}
              {translate(resources.newsletter.banners.footer.text1)}{' '}
              <Link
                to="/politica-prelucrare-date-personale"
                target="_blank"
                rel="noopener noreferrer"
              >
                {translate(
                  resources.newsletter.banners.footer.privacyPolicyLink,
                )}
              </Link>
              {customerInfo.languageCode === 'ro' ? ' ' : ''}
              {translate(resources.newsletter.banners.footer.text2)}{' '}
              <Link to="/my-account/preferences">
                {translate(
                  resources.newsletter.banners.footer
                    .communicationPreferencesLink,
                )}
              </Link>
              {customerInfo.languageCode === 'ro' ? '' : ' '}
              {translate(resources.newsletter.banners.footer.text3)}
            </div>
          </div>

          <div className="dialog-actions vertical full">
            <Button
              className="btn btn-solid"
              loading={isSubscribing}
              disabled={isDeclining}
              onClick={handleSubscribe}
            >
              {translate(resources.newsletter.dialog.actions.subscribe)}
            </Button>

            <Button
              onClick={handleDecline}
              className="btn btn-outline"
              disabled={isDeclining || isSubscribing}
            >
              {translate(resources.newsletter.dialog.actions.notNow)}
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
